import useFeatureFlagByNameKey from '@/shared/hooks/useFeatureFlagByNameKey/useFeatureFlagByNameKey';

import { useCorporateOrganizations } from '../use-corporate-organizations/use-corporate-organizations';
import { useGetCentralizedSessionData } from '../use-get-centralized-session-data';

export type TUseCentralizedCorporateNavigation = ReturnType<
  typeof useCentralizedCorporateNavigation
>;

export function useCentralizedCorporateNavigation(
  organizationSlug: string | undefined,
) {
  const {
    userOrganizationSlug,
    isCentralizedCorporationSuperAdmin,
    loading: isSessionDataLoading,
    error: errorSessionData,
  } = useGetCentralizedSessionData(
    organizationSlug || '',
    Boolean(!organizationSlug),
  );

  const {
    valueFeatureFlag: isCentralizedChildrenOrganizationManagementEnabled,
    loading: isFeatureFlagLoading,
    error: errorFeatureFlag,
  } = useFeatureFlagByNameKey('ENABLE_CHILDREN_ORGANIZATION_MANAGEMENT');

  /**
   * If the FF is enabled and the user is a super admin (and everything else is ok), we can continue
   */
  const isEnabled =
    !isFeatureFlagLoading &&
    !errorFeatureFlag &&
    isCentralizedChildrenOrganizationManagementEnabled &&
    !isSessionDataLoading &&
    !errorSessionData &&
    isCentralizedCorporationSuperAdmin;

  const isEnabledLoading = isFeatureFlagLoading || isSessionDataLoading;

  const {
    isCorporation,
    parentOrganizationId,
    loading: isCorporateOrganizationsLoading,
    error: isCorporateOrganizationsError,
  } = useCorporateOrganizations({
    slug: organizationSlug || '',
    skip: isEnabledLoading || !isEnabled,
  });

  const isLoading = isEnabledLoading || isCorporateOrganizationsLoading;
  const error = isCorporateOrganizationsError;

  if (isLoading) {
    return {
      isCentralizedCorporation: false,
      isCorporateSuperAdmin: false,
      userOrganizationSlug: undefined,
      isLoading: true,
      error: undefined,
    };
  }

  if (error) {
    return {
      isCentralizedCorporation: false,
      isCorporateSuperAdmin: false,
      userOrganizationSlug: undefined,
      isLoading: false,
      error: error,
    };
  }

  /**
   * If the user is a centralized super admin and the feature flag is enabled,
   * and this is a corporation.
   */
  const isCentralizedCorporation =
    isEnabled &&
    (isCorporation ||
      // This shouldnt be required but isCorporation is currently true only for
      // the parent organization, overlapping with isCurrentParentOrg instead of
      // telling if the organization belongs to a corporation
      !!parentOrganizationId);

  /**
   * If the user is a centralized super admin and the feature flag is enabled,
   * and this is a corporation and the super admin is registered in the parent organization,
   * then the user can view the corporate features.
   */
  const isCorporateSuperAdmin =
    isCentralizedCorporation && isCentralizedCorporationSuperAdmin;

  return {
    isCentralizedCorporation,
    isCorporateSuperAdmin,
    userOrganizationSlug,
    isLoading,
    error: errorFeatureFlag || errorSessionData,
  };
}
